(function () {
  "use strict";

  function normalizeSearch(value) {
    return String(value || "")
      .normalize("NFKC")
      .toLocaleLowerCase()
      .trim()
      .replace(/\s+/g, " ");
  }

  function readConfig(root) {
    const node = root.querySelector("[data-fw-faqs-config]");
    if (!node) return { defaultCategory: "all", allHeading: "All Topics", categoryLabels: {} };
    try {
      return JSON.parse(node.textContent);
    } catch (_e) {
      return { defaultCategory: "all", allHeading: "All Topics", categoryLabels: {} };
    }
  }

  function getItemSearchText(item) {
    const question = item.querySelector(".fw-faqs-page__question")?.textContent || "";
    const answer = item.querySelector("[data-fw-faqs-answer-text]")?.textContent || "";
    return normalizeSearch(`${question} ${answer}`);
  }

  function setItemOpen(item, open) {
    const button = item.querySelector("[data-fw-faq-toggle]");
    const panel = item.querySelector("[data-fw-faq-panel]");
    if (!button || !panel) return;
    item.classList.toggle("is-open", open);
    button.setAttribute("aria-expanded", open ? "true" : "false");
    panel.hidden = !open;
  }

  function collapseAll(items) {
    items.forEach((item) => setItemOpen(item, false));
  }

  function bindAccordion(items) {
    items.forEach((item) => {
      const button = item.querySelector("[data-fw-faq-toggle]");
      if (!button || button.dataset.fwFaqsBound === "true") return;
      button.dataset.fwFaqsBound = "true";
      button.addEventListener("click", () => {
        const open = !item.classList.contains("is-open");
        collapseAll(items);
        setItemOpen(item, open);
      });
    });
  }

  function initFaqsPage(root) {
    if (!root || root.dataset.fwFaqsInit === "true") return;
    root.dataset.fwFaqsInit = "true";

    const config = readConfig(root);
    const items = Array.from(root.querySelectorAll("[data-fw-faqs-item]"));
    const filters = Array.from(root.querySelectorAll("[data-fw-faqs-filter]"));
    const searchInput = root.querySelector("[data-fw-faqs-search]");
    const searchSubmit = root.querySelector("[data-fw-faqs-search-submit]");
    const searchClear = root.querySelector("[data-fw-faqs-search-clear]");
    const emptyEl = root.querySelector("[data-fw-faqs-empty]");
    const headingEl = root.querySelector("[data-fw-faqs-category-heading]");
    const countNodes = Array.from(root.querySelectorAll("[data-fw-faqs-count]"));

    const totals = { all: items.length };
    items.forEach((item) => {
      const cat = item.getAttribute("data-category") || "";
      totals[cat] = (totals[cat] || 0) + 1;
      item.dataset.fwFaqsSearchText = getItemSearchText(item);
    });

    const initialCategory =
      root.getAttribute("data-initial-category") || config.defaultCategory || "all";
    let activeCategory = initialCategory;
    let query = "";

    function categoryHeading(category) {
      if (category === "all") return config.allHeading || "All Topics";
      return config.categoryLabels?.[category] || category;
    }

    function matchesFilters(item) {
      const cat = item.getAttribute("data-category") || "";
      const categoryMatches = activeCategory === "all" || cat === activeCategory;
      const q = normalizeSearch(query);
      const searchMatches = !q || (item.dataset.fwFaqsSearchText || "").includes(q);
      return categoryMatches && searchMatches;
    }

    function updateCounts(visibleItems) {
      const q = normalizeSearch(query);
      countNodes.forEach((node) => {
        const key = node.getAttribute("data-fw-faqs-count");
        if (!key) return;
        if (!q) {
          node.textContent = String(totals[key] || 0);
          return;
        }
        if (key === "all") {
          node.textContent = String(visibleItems.length);
          return;
        }
        const n = visibleItems.filter((item) => item.getAttribute("data-category") === key).length;
        node.textContent = String(n);
      });
    }

    function applyFilters(options = {}) {
      const visible = [];
      items.forEach((item) => {
        const show = matchesFilters(item);
        item.hidden = !show;
        item.classList.toggle("is-filtered-out", !show);
        if (show) {
          item.removeAttribute("inert");
          visible.push(item);
        } else {
          item.setAttribute("inert", "");
          collapseAll([item]);
        }
      });

      if (headingEl) {
        headingEl.textContent = categoryHeading(activeCategory);
      }

      filters.forEach((btn) => {
        const key = btn.getAttribute("data-fw-faqs-filter");
        const active = key === activeCategory;
        btn.classList.toggle("is-active", active);
        btn.setAttribute("aria-pressed", active ? "true" : "false");
      });

      collapseAll(items);
      if (visible.length) {
        setItemOpen(visible[0], true);
        if (emptyEl) emptyEl.hidden = true;
      } else if (emptyEl) {
        emptyEl.hidden = false;
      }

      updateCounts(visible);

      if (searchClear) {
        searchClear.hidden = !normalizeSearch(query);
      }

      if (options.announce && visible.length) {
        root.setAttribute("data-fw-faqs-results", String(visible.length));
      }
    }

    bindAccordion(items);

    filters.forEach((btn) => {
      btn.addEventListener("click", () => {
        activeCategory = btn.getAttribute("data-fw-faqs-filter") || "all";
        applyFilters();
      });
    });

    const onSearchInput = () => {
      query = searchInput?.value || "";
      applyFilters();
    };

    searchInput?.addEventListener("input", onSearchInput);
    searchInput?.addEventListener("keydown", (event) => {
      if (event.key === "Escape") {
        query = "";
        if (searchInput) searchInput.value = "";
        applyFilters();
        searchInput.blur();
      }
    });

    searchSubmit?.addEventListener("click", () => {
      query = searchInput?.value || "";
      applyFilters({ announce: true });
      searchInput?.focus();
    });

    searchClear?.addEventListener("click", () => {
      query = "";
      if (searchInput) searchInput.value = "";
      applyFilters();
      searchInput?.focus();
    });

    applyFilters();
  }

  function boot() {
    document.querySelectorAll("[data-fw-faqs-page]").forEach(initFaqsPage);
  }

  document.addEventListener("DOMContentLoaded", boot);
  document.addEventListener("shopify:section:load", (event) => {
    const section = event.target;
    if (!(section instanceof Element)) return;
    section.querySelectorAll("[data-fw-faqs-page]").forEach((root) => {
      root.dataset.fwFaqsInit = "";
      initFaqsPage(root);
    });
  });
})();
